import type { GoogleAdsConfig, TrackingConfig } from './types';

const META_PIXEL_ID = /^\d{10,20}$/;
const REDDIT_PIXEL_ID = /^[A-Za-z0-9_]{4,40}$/;
const GOOGLE_ADS_ID = /^AW-\d{6,15}$/;
const GOOGLE_ADS_LABEL = /^[A-Za-z0-9_-]{4,40}$/;
const GA4_MEASUREMENT_ID = /^G-[A-Z0-9]{4,15}$/;
const CURRENCY_CODE = /^[A-Z]{3}$/;

type RawRecord = Record<string, unknown>;

function asRecord(value: unknown): RawRecord | null {
  return value !== null && typeof value === 'object' ? (value as RawRecord) : null;
}

// The backend validates every id; this guards the script URLs and vendor
// calls they end up in, so a malformed one is dropped, not passed along.
function matching(value: unknown, pattern: RegExp): string | null {
  return typeof value === 'string' && pattern.test(value) ? value : null;
}

function parseGoogleAds(raw: unknown): GoogleAdsConfig | null {
  const ads = asRecord(raw);
  const conversionId = matching(ads?.conversion_id, GOOGLE_ADS_ID);
  const purchaseLabel = matching(ads?.purchase_label, GOOGLE_ADS_LABEL);
  if (!conversionId || !purchaseLabel) return null;
  return {
    conversionId,
    purchaseLabel,
    checkoutLabel: matching(ads?.checkout_label, GOOGLE_ADS_LABEL),
  };
}

/** design_system `tracking_pixels` → config, or null when no provider is usable. */
export function parseTrackingConfig(raw: unknown): TrackingConfig | null {
  const source = asRecord(raw);
  if (!source) return null;
  const config: TrackingConfig = {
    metaPixelId: matching(asRecord(source.meta)?.pixel_id, META_PIXEL_ID),
    redditPixelId: matching(asRecord(source.reddit)?.pixel_id, REDDIT_PIXEL_ID),
    googleAds: parseGoogleAds(source.google_ads),
    ga4MeasurementId: matching(asRecord(source.ga4)?.measurement_id, GA4_MEASUREMENT_ID),
    currencyOverride: matching(source.currency_override, CURRENCY_CODE),
  };
  const hasProvider =
    config.metaPixelId !== null ||
    config.redditPixelId !== null ||
    config.googleAds !== null ||
    config.ga4MeasurementId !== null;
  return hasProvider ? config : null;
}
